import { Info } from '@mui/icons-material';
import { useStyles } from './styles';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import { numberToCurrencyString } from 'further-ui/utils';
import { Tooltip } from '@mui/material';
import { StagedShareholding } from '../../ExitDetailsForm/types';

interface AccruedFeesCellProps {
  cell: StagedShareholding & {
    originalAccruedFeesCharged?: number;
    accruedFeesSetBelowTarget?: boolean;
  };
  setOverride: (
    shareholdingId: string,
    overrides: Partial<StagedShareholding>,
  ) => void;
  disableTable?: boolean;
  subscriptionError?: { exceedsLimit: boolean };
}

const AccruedFeesCell: React.FC<AccruedFeesCellProps> = ({
  cell,
  setOverride,
  disableTable,
  subscriptionError,
}) => {
  const { classes } = useStyles();
  // For amendment corrections, cap is original accrued fees charged in this amendment + remaining available
  // Otherwise, cap is investorTotalAccruedFees (investment-level max, not per-shareholding limit)
  // This allows users to manually split accrued fees between shareholdings of the same investment,
  // with the backend validation catching any investment-level overspend.
  const maxAccruedFees =
    cell?.totalAvailableAccruedFees !== undefined
      ? (cell.originalAccruedFeesCharged ?? cell.accruedFeesCharged ?? 0) +
        (cell?.totalAvailableAccruedFees ?? 0)
      : cell?.investorTotalAccruedFees ?? 0;

  const isInputTooHigh = (cell?.accruedFeesCharged ?? 0) > maxAccruedFees;
  const isSubscriptionTooHigh =
    (subscriptionError?.exceedsLimit ?? false) &&
    (cell?.accruedFeesCharged ?? 0) > 0;

  const helperText = isInputTooHigh
    ? `This investor only has ${numberToCurrencyString(
        maxAccruedFees,
      )} in accrued fees`
    : isSubscriptionTooHigh
    ? `Combined accrued fees for this subscription exceeds the limit of ${numberToCurrencyString(
        maxAccruedFees,
      )}`
    : '';

  const diffMessage =
    cell.accruedFeesSetBelowTarget &&
    "This investor did not have sufficient exited capital for the requested accrued fee percentage to be charged. As much of the investor's accrued fees as possible have been charged.";

  return (
    <div className={classes.flex}>
      <AppNumberInput
        disabled={disableTable}
        decimals={2}
        placeholder="0"
        defaultValue={0}
        min={0}
        currencyStartAdornment
        value={cell.accruedFeesCharged}
        onChange={(newValue) => {
          setOverride(cell.shareholdingId, {
            accruedFeesCharged: Number(newValue),
          });
        }}
        error={isInputTooHigh || isSubscriptionTooHigh}
        helperText={helperText}
      />
      {diffMessage && (
        <Tooltip title={diffMessage}>
          <Info style={{ fontSize: 18 }} />
        </Tooltip>
      )}
    </div>
  );
};

export default AccruedFeesCell;
